// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { QtyFieldStyles } from "@ces/App/Components/Qty/QtyField";
import { xIsAuthenticated } from "@ces/App/Store/Selectors/GeneralSelectors";
import ProductQuantityOrdered from "@ces/Overrides/Components/ProductQuantityOrdered";
import { mergeWithCss } from "@ces/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { SelectPresentationProps } from "@insite/mobius/Select";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";

import * as React from "react";
import { FC, useState } from "react";
import { connect } from "react-redux";

export interface ProductActionsProps {
    carouselId?: string;
    iconOnly?: boolean;
    showActions?: boolean;
    extendedStyles?: ProductActionsStyle;
    onAddToCartClick?: () => void;
    currentPage?: string;
    showDisabled?: boolean;
    hideUOM?: boolean;
    secure?: boolean;
    // XNG-Change
    showTooltip?: boolean;
    useDefaultUomBehavior?: boolean;
}

type Props = ProductActionsProps & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface ProductActionsStyle {
    addToCartContainer?: GridContainerProps;
    qtyWrapper?: GridItemProps;
    addToCartButtonWrapper?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityOrderedStyles?: QtyFieldStyles;
    productUomWrapper?: InjectableCss;
}

export const productActionsStyle: ProductActionsStyle = {};

const mapStateToProps = (state: ApplicationState) => ({
    currentPage: getCurrentPage(state)?.type,
    isAuthenticated: xIsAuthenticated(state),
});
const ProductActions: FC<Props> = ({
    carouselId,
    iconOnly,
    extendedStyles,
    showActions = true,
    onAddToCartClick,
    currentPage,
    showDisabled,
    hideUOM,
    isAuthenticated,
    secure,
    productContext: {
        product: { detail, minimumOrderQty, unitOfMeasures },
    },
    // XNG-Change
    showTooltip = false,
    useDefaultUomBehavior,
}) => {
    if (secure && !isAuthenticated) {
        return null;
    }
    const [styles] = useState(() => mergeWithCss(productActionsStyle, extendedStyles));
    return (
        <StyledWrapper {...styles.addToCartContainer} data-test-selector="product_actions">
            <StyledWrapper {...styles.qtyWrapper}>
                <VisuallyHidden as="label" htmlFor={`${carouselId}-qty`} id={`${carouselId}-qty-label`}>
                    {translate("QTY")}
                </VisuallyHidden>
                <ProductQuantityOrdered
                    displayTooltip={showTooltip}
                    data-test-selector="productQty"
                    extendedStyles={styles.productQuantityOrderedStyles}
                />
            </StyledWrapper>
            {unitOfMeasures && (
                <StyledWrapper {...styles.productUomWrapper}>
                    <ProductUnitOfMeasureSelect
                        labelOverride=""
                        extendedStyles={styles.productUnitOfMeasureSelectStyles}
                        id={`${carouselId}-uom`}
                        {...{ isDefaultBehavior: useDefaultUomBehavior }}
                    />
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.addToCartButtonWrapper}>
                <ProductAddToCartButton
                    {...{ iconOnly }}
                    onAddToCartClick={onAddToCartClick}
                    extendedStyles={styles.productAddToCartButtonStyles}
                    data-test-selector="addToCartBtn"
                    {...{ showMultipleSaleQtyTooltip: detail?.multipleSaleQty ? detail.multipleSaleQty > 1 : false }}
                />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductActions));
